import React, { useEffect } from 'react';
import SessionHelper from '../../Helper/SessionHelper';
import { TtsSwirl } from '../../redesign';

/**
 * LogoutPage — clears the local session + revokes the Cognito session
 * so the next login gets fresh claims, then redirects to /login.
 *
 * The branded card here only appears for the brief moment between
 * "logout started" and the Cognito logout-URL redirect (or the
 * /login bounce when no refresh token is on file). It mirrors the
 * SSO callback recipe so logging out and logging back in feel like
 * the same flow visually.
 */
function LogoutPage() {
  useEffect(() => {
    async function performLogout() {
      // Only SSO sessions have a Cognito session to revoke; an email
      // login's refresh token belongs to APIv2.
      const refreshToken = SessionHelper.isSsoSession() ? SessionHelper.getTtsRefreshToken() : null;
      SessionHelper.logout();

      // Redirect through Cognito logout to clear the browser session
      // cookie so the next login gets a fresh Cognito session with
      // up-to-date claims.
      if (refreshToken) {
        const logoutUrl = await SessionHelper.revokeCognitoSession(
          refreshToken,
          window.location.origin,
        );
        if (logoutUrl) {
          window.location.href = logoutUrl;
          return;
        }
      }

      window.location.href = '/login';
    }

    performLogout();
  }, []);

  return (
    <div
      className="bg-bg min-h-screen flex items-center justify-center"
      style={{ padding: '24px 16px' }}
    >
      <div
        className="bg-surface border border-sand"
        style={{
          width: '100%',
          maxWidth: 420,
          borderRadius: 14,
          boxShadow: '0 12px 32px #2C3E5018, 0 2px 6px #2C3E500A',
          padding: '36px 32px',
          textAlign: 'center',
        }}
      >
        <div
          style={{
            width: 64,
            height: 64,
            margin: '0 auto 18px',
            // Same pulse beat as the callback page so the two screens
            // feel like the same brand flow rather than two unrelated
            // spinners. Slightly more subtle here (lower amplitude)
            // because logout is reassuring, not anticipatory.
            animation: 'ttsSwirlPulseLogout 1.8s ease-in-out infinite',
          }}
        >
          <TtsSwirl style={{ width: '100%', height: '100%' }} />
        </div>

        <div
          className="font-condensed font-bold uppercase"
          style={{
            fontSize: 11,
            letterSpacing: '0.12em',
            color: '#5C7E72',
            marginBottom: 6,
          }}
        >
          Signing you out
        </div>

        <h1
          className="text-navy"
          style={{
            fontSize: 22,
            fontWeight: 700,
            margin: '0 0 10px',
            letterSpacing: '-0.01em',
            lineHeight: 1.25,
          }}
        >
          See you later
        </h1>

        <p
          className="text-text-muted"
          style={{ fontSize: 13, lineHeight: 1.5, margin: 0 }}
        >
          Clearing your session — you&apos;ll be back at the login screen in a moment.
        </p>
      </div>

      <style>{`
        @keyframes ttsSwirlPulseLogout {
          0%, 100% { transform: scale(1);    opacity: 1;    }
          50%      { transform: scale(1.03); opacity: 0.78; }
        }
      `}</style>
    </div>
  );
}

export default LogoutPage;
